import { useEffect } from 'react';

/**
 * Which company a per-company finance screen is showing.
 *
 * Billing and Staff payout used to open on `list[0]` — whichever company the
 * registry happened to return first, which on the live platform was a ₹0
 * fixture company while the one with real invoices sat at the bottom of the
 * picker (audit of 1 Oct 2026). A screen full of zeroes for a company nobody
 * chose reads as "nothing has been billed", which is a wrong answer rather
 * than no answer.
 *
 * So the company is, in order:
 *   1. the one the link names (`?company=`) — a link from Payment review or a
 *      company's page must land where it points;
 *   2. the one last chosen in this browser, on either screen;
 *   3. the only one, when there is exactly one;
 *   4. nobody — the screen asks, and shows nothing until it is told.
 *
 * There is deliberately no "most recently invoiced" default. Invoices are
 * each company's own rows, so finding it means opening every company in turn
 * on each page load; the last choice is nearly always the same answer, free.
 *
 * The memory is per browser and a convenience only: every request still names
 * its company and the API binds and audits each one.
 */
const KEY = 'dru.admin.financeCompany';

export function remembered(): string | null {
  // Storage can be unavailable (private mode, blocked site data); the screen
  // then simply asks each time.
  try { return localStorage.getItem(KEY); } catch { return null; }
}

function remember(id: string) {
  try { localStorage.setItem(KEY, id); } catch { /* asks again next time */ }
}

export type CompanyChoice = { id: string; source: 'link' | 'remembered' | 'only' | 'none' };

/** Pure, so the order above is one function rather than a habit two screens each have to keep. */
export function resolveCompany(list: Array<{ id: string }>, asked: string | null, last: string | null): CompanyChoice {
  const has = (id: string | null): id is string => !!id && list.some((c) => c.id === id);
  if (has(asked)) return { id: asked, source: 'link' };
  if (has(last)) return { id: last, source: 'remembered' };
  if (list.length === 1) return { id: list[0]!.id, source: 'only' };
  return { id: '', source: 'none' };
}

/** The company to show, and remembered for next time once it is known. */
export function useCompanyChoice(list: Array<{ id: string }>, asked: string | null): CompanyChoice {
  const choice = resolveCompany(list, asked, remembered());
  useEffect(() => { if (choice.id) remember(choice.id); }, [choice.id]);
  return choice;
}
